"use client";

import { useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import {
  CONTENTSQUARE_TAG_ID,
  buildTrackingPath,
  ensureQueue,
  isAuthPath,
  isTrackingEnabled,
  pushCommand,
} from "@/lib/contentsquare";

const CONTENTSQUARE_SRC = `https://t.contentsquare.net/uxa/${CONTENTSQUARE_TAG_ID}.js`;

/** Survives React Strict Mode remounts so the tag is only bootstrapped once. */
let hasBootstrappedTag = false;
let lastTrackedPath: string | null = null;
/** Set when the tag script fails to load (blocked, offline) so we stop queueing forever. */
let tagLoadFailed = false;

/**
 * Official Contentsquare main tag behavior for SPAs:
 * - First load: setPath + inject script
 * - Later navigations: trackPageview
 *
 * The reported page URL is normalized (`/:companyId/dashboard`) and carries only the
 * query params that define the page, so heatmaps are not split per client or per filter.
 * Auth routes are excluded so login/register are not recorded.
 */
export default function ContentsquareInit() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const onAuthRoute = isAuthPath(pathname);
  const search = searchParams?.toString() ?? "";

  useEffect(() => {
    if (!isTrackingEnabled() || onAuthRoute || tagLoadFailed) return;
    if (typeof window === "undefined" || typeof document === "undefined") return;

    ensureQueue();
    const trackingPath = buildTrackingPath(pathname, search);

    const tagAlreadyPresent =
      hasBootstrappedTag ||
      typeof window.CS_CONF !== "undefined" ||
      Boolean(document.getElementById("contentsquare"));

    if (!tagAlreadyPresent) {
      pushCommand("setPath", trackingPath);

      const script = document.createElement("script");
      script.id = "contentsquare";
      script.type = "text/javascript";
      script.async = true;
      script.src = CONTENTSQUARE_SRC;
      script.onerror = () => {
        // Nothing will ever drain `_uxa` — stop tracking instead of growing the queue.
        tagLoadFailed = true;
        hasBootstrappedTag = false;
        script.remove();
      };
      document.head.appendChild(script);

      hasBootstrappedTag = true;
      lastTrackedPath = trackingPath;
      return;
    }

    hasBootstrappedTag = true;

    // Skip duplicates from React Strict Mode remounts and from query changes that do
    // not alter the tracked path (date ranges, filters).
    if (lastTrackedPath === trackingPath) return;

    lastTrackedPath = trackingPath;
    pushCommand("trackPageview", trackingPath);
  }, [pathname, search, onAuthRoute]);

  return null;
}
